// useShellRouting — the shell's side of the URL seam (F48): the new-chat
// transitions threaded into useUrlNavigation, the 404 gate's way out, and the
// useUrlNavigation mount itself. Split out of useShellFoundation (size gate)
// without changing behaviour: called once, unconditionally, at the position
// the block occupied in the foundation body, so the hook-call order is
// unchanged.
export { useShellRouting };

import type { TabSet } from "../../components/Navigation/types.js";
import type { RecentThread } from "../../components/WorkspaceLanding/WorkspaceLanding.js";
import { type ConversationList, durabilityOf } from "../conversationSummary.js";
import type { ExternalError } from "../externalError.js";
import type { FrameTransition } from "../frame.js";
import {
    type UrlNavDeps,
    useUrlNavigation,
} from "../route/useUrlNavigation.js";

type ShellRoutingDeps = Pick<
    UrlNavDeps,
    "frame" | "dispatch" | "refFor" | "notFound" | "setNotFound"
> & {
    readonly tabs: TabSet;
    readonly conversationList: ConversationList<RecentThread, ExternalError>;
    readonly conversationIsDurable: (conversationId: string) => boolean;
};

const useShellRouting = ({
    frame,
    dispatch,
    refFor,
    notFound,
    setNotFound,
    tabs,
    conversationList,
    conversationIsDurable,
}: ShellRoutingDeps) => {
    // The transitions that land on a NEW CHAT (`/`): rest on an already-open
    // NASCENT conversation, else mint a fresh one (ids never in the pure fold).
    // Threaded into the URL seam so booting `/` shows a new chat authoritatively.
    const chatNav = (): readonly FrameTransition[] => {
        const active = tabs.active;
        if (active.type === "conversation" && !conversationIsDurable(active.id))
            return [];
        const nascent = tabs.conversationsOpen.find(
            (id) => !conversationIsDurable(id),
        );
        return nascent !== undefined
            ? [
                  {
                      type: "activate-tab",
                      tab: { type: "conversation", id: nascent },
                  },
              ]
            : [
                  {
                      type: "new-tab",
                      conversationId: globalThis.crypto.randomUUID(),
                  },
              ];
    };
    // Always-mint a fresh new chat — for BOOT `/`, which must supersede the
    // restored active focus with a guaranteed-nascent conversation (durability
    // is unknown at boot, so a reuse could wrongly land on a restored durable
    // conversation and bounce the bar to `/c/:id`).
    const startNewChat = (): readonly FrameTransition[] => [
        { type: "new-tab", conversationId: globalThis.crypto.randomUUID() },
    ];
    // The 404 gate's one way out (`/` = new chat): lower the gate + navigate.
    // Post-boot, so `chatNav`'s reuse is safe (durability is known).
    const onRouteHome = (): void => {
        setNotFound(false);
        for (const t of chatNav()) dispatch(t);
    };
    // The pushState/popstate seam — the pure Route layer mirrored into the bar
    // and back. The frame decider stays the single writer; this reads it (to
    // push) and dispatches into it (on pop/boot), resolving the state-dependent
    // arms (chat/resources/quality/notFound) with the deps below.
    useUrlNavigation({
        frame,
        dispatch,
        refFor,
        conversationDurability: (id) => durabilityOf(conversationList, id),
        chatNav,
        startNewChat,
        notFound,
        setNotFound,
    });

    return { onRouteHome };
};
